import { Injectable, Injector, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { HrAuthService } from '../services/hr-auth.service';
import { HrFederationService } from '../services/hr-federation.service';
import { HrSessionService } from '../services/hr-session.service';

/** Response header with the new end of the HR elevation, set by elevated requests */
const ELEVATION_UNTIL_HEADER = 'HR-Elevation-Until';

/**
 * HTTP interceptor for HR API requests.
 *
 * - Public account bootstrap (`/account/login`, register, reset, SSO exchange): no auth injection
 * - All other HR endpoints: the Caresonal session token from the federation exchange. The
 *   HR elevation is part of that session, so there is no separate HR token.
 * - A rejected session token (401) is discarded and exchanged once, then the request repeats.
 * - `HR_ELEVATION_REQUIRED` opens the MFA dialog in this tab, then the request repeats.
 *
 * Uses lazy Injector resolution to avoid NG0200 circular DI.
 */
@Injectable()
export class HrJwtInterceptor implements HttpInterceptor {
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);

  private hrElevationInFlight: Promise<boolean> | null = null;

  private get hrAuthService(): HrAuthService {
    return this.injector.get(HrAuthService);
  }

  private get hrFederationService(): HrFederationService {
    return this.injector.get(HrFederationService);
  }

  private get hrSessionService(): HrSessionService {
    return this.injector.get(HrSessionService);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!environment.hrApiUrl || environment.hrApiUrl.trim() === '') {
      return next.handle(req);
    }

    const hrApiUrl = environment.hrApiUrl.replace(/\/+$/, '');
    const isHrRequest = hrApiUrl && (req.url.startsWith(hrApiUrl) || req.url.includes(hrApiUrl));

    if (!isHrRequest) {
      return next.handle(req);
    }

    // Public auth bootstrap — login / register / password reset / SSO exchange
    if (this.isPublicAccountEndpoint(req.url)) {
      return next.handle(req);
    }

    // Caller-supplied Authorization, e.g. the logout of a session that is being left
    if (req.headers.has('Authorization')) {
      return next.handle(req);
    }

    const token = this.hrFederationService.token;

    return this.handleWithSession(req, next, token).pipe(
      catchError((error: HttpErrorResponse) => this.handleSessionError(error, req, next, token))
    );
  }

  private handleWithSession(req: HttpRequest<any>, next: HttpHandler, token: string | null): Observable<HttpEvent<any>> {
    const authedReq = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

    return next.handle(authedReq).pipe(
      tap((event) => this.takeOverElevation(event)),
      catchError((error: HttpErrorResponse) => this.handleElevationError(error, req, next))
    );
  }

  private isPublicAccountEndpoint(url: string): boolean {
    return (
      url.includes('/account/login') ||
      url.includes('/account/register') ||
      url.includes('/account/resetPassword') ||
      url.includes('/account/sso/')
    );
  }

  /** Each elevated request extends the elevation on the server; the header reports the new end */
  private takeOverElevation(event: HttpEvent<any>): void {
    if (!(event instanceof HttpResponse)) return;

    const elevatedUntil = Number(event.headers.get(ELEVATION_UNTIL_HEADER));
    if (elevatedUntil) this.hrAuthService.extendElevation(elevatedUntil);
  }

  /**
   * The session may have ended on the server (idle time, maximum, revoked). Discard the token,
   * exchange once and repeat the request with the new one.
   */
  private handleSessionError(
    error: HttpErrorResponse,
    req: HttpRequest<any>,
    next: HttpHandler,
    token: string | null
  ): Observable<HttpEvent<any>> {
    if (error.status !== 401 || !this.hrFederationService.isFederationEnabled || req.url.includes('/account/logout')) {
      return throwError(() => error);
    }

    this.hrFederationService.discard(token);

    return from(this.hrFederationService.exchange()).pipe(
      switchMap((exchanged) => {
        const newToken = this.hrFederationService.token;
        if (!exchanged || !newToken) return throwError(() => error);

        return this.handleWithSession(req, next, newToken);
      })
    );
  }

  private handleElevationError(
    error: HttpErrorResponse,
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const needsElevation =
      error.error?.errorCode === 'HR_ELEVATION_REQUIRED' ||
      error.error?.errorCode === 'HR_MFA_REQUIRED';

    if (!needsElevation) {
      return throwError(() => error);
    }

    // The elevation has ended on the server, in every tab
    this.hrAuthService.clearElevation();

    return from(this.ensureElevation()).pipe(
      switchMap((elevated) => {
        const token = this.hrFederationService.token;
        if (!elevated || !token) {
          return throwError(() => error);
        }

        return next.handle(req.clone({
          setHeaders: { Authorization: `Bearer ${token}` }
        })).pipe(tap((event) => this.takeOverElevation(event)));
      })
    );
  }

  private ensureElevation(): Promise<boolean> {
    if (this.hrAuthService.isElevatedSession()) {
      return Promise.resolve(true);
    }

    if (this.hrElevationInFlight) {
      return this.hrElevationInFlight;
    }

    const flow = this.runHrMfaFlow();
    this.hrElevationInFlight = flow.finally(() => {
      this.hrElevationInFlight = null;
    });

    return this.hrElevationInFlight;
  }

  /** Opens the MFA dialog only in this tab; the others take the elevation over via localStorage */
  private async runHrMfaFlow(): Promise<boolean> {
    try {
      const status = await this.hrSessionService.refreshEligibility();

      if (this.hrAuthService.isElevatedSession()) {
        return true;
      }

      const result = await this.hrSessionService.openElevationDialog(status);
      if (!result) {
        this.router.navigate(['/user/dashboard']);
      }
      return result;
    } catch {
      return false;
    }
  }
}
